<script lang="ts">
const theme = useTheme()
</script>

<script setup lang="ts">
interface Props {
  color?: string
  colorHover?: string
  hideIcon?: boolean
  activeMenu?: boolean
}

interface IEmits {
  (event: 'click', e: { panelActive: boolean }): void
}

const {
  color = theme.colors.softBlack,
  colorHover = theme.colors.softGray,
  activeMenu = false,
} = defineProps<Props>()
const emit = defineEmits<IEmits>()

const panelHover = ref(false)
const panelActive = ref(activeMenu)

const computedTextColor = computed(() => panelHover.value ? colorHover : color)

const click = () => {
  panelActive.value = !panelActive.value
  emit('click', { panelActive: panelActive.value })
}

watch(
  () => activeMenu,
  (newValue: boolean, oldValue: boolean) => {
    if (newValue !== oldValue)
      panelActive.value = newValue
  },
)
</script>

<template>
  <div
    class="expansion-panel"
    @mouseover="panelHover = true"
    @mouseleave="panelHover = false"
  >
    <div
      class="label"
      :style="{
        color: computedTextColor,
      }"
      @click="click"
    >
      <span>
        <slot />
      </span>
      <div class="w-[32px] flex justify-center items-center expansion-panel-arrow">
        <svg
          v-if="!hideIcon"
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="11"
          viewBox="0 0 18 11"
          fill="none"
          class="w-[15.188px] h-[7.594px] transition-all duration-150 ease-in-out"
          :class="panelActive ? 'arrow-active rotate-180' : 'rotate-0'"
        >
          <path d="M1.40625 1.55469L9 9.14844L16.5938 1.55469" :stroke="computedTextColor" stroke-width="2.3625" />
        </svg>
      </div>
    </div>

    <TransitionCollapse>
      <div
        v-if="panelActive"
        class="expansion-panel-content"
        @click.stop
      >
        <slot name="expansion-panel-content" />
      </div>
    </TransitionCollapse>
  </div>
</template>

<style lang="scss" scoped>
.font-style {
  @apply text-[0.75rem] font-[500] leading-[1.3125rem];
  font-family: 'Inter', sans-serif;
}

.expansion-panel {
  @apply w-full flex flex-col justify-between items-start;
  @apply overflow-hidden relative transition-all ease-in-out duration-150;
}

.label {
  @extend .font-style;
  @apply relative w-full flex justify-between items-center min-h-[2.5rem] flex-row pr-1;
  cursor: pointer;
}

.expansion-panel-active {
  @apply w-full relative;
}

.expansion-panel-content {
  @apply w-full relative;
}
</style>
